import "./style.scss";
import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { IApplyInfoData, IRowData } from "@/src/types/applicationOnline";
import { IOfficeAccountingTableRow } from "@/src/types/officeAccounting";
import { ITasksTableRow } from "@/src/types/tasksTable";
import { applicantHistoryRequests } from "@/src/api/applicantHistoryRequests";
import { applicantsRequests } from "@/src/api/applicantsRequests";
import { IStudentUpdatePayload } from "@/src/api/studentsRequests";
import { IApplicantUpdatePayload } from "@/src/types/applicant";
import { applicationPageSvgConst } from "@/src/svg/applicationsPage/APPopUp/APPopUpSvgExport";
import { onlineApplicationsPopUpTabs } from "@/src/constants/applicationOnlineConsts";
import BtnDefault from "@/src/components/BtnDefault/BtnDefault";
import Arrow from "@/src/assets/sidebar/Arrow.png";
import CustomDescriptionPopUP from "@/src/components/CustomUtils/CustomDescriptionPopUP/CustomDescriptionPopUP";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@/src/store";
import { toggleRowSelection as toggleApplicationRowSelection } from "@/src/store/slices/ApplicationPage/applicationsTableSlice";
import { toggleRowSelection as toggleOfficeAccountingRowSelection } from "@/src/store/slices/OfficeAccountingPage/officeAccountingTableSlice";
import { toggleRowSelection as toggleTasksRowSelection } from "@/src/store/slices/TasksPage/tasksTableSlice";
import GeneralInformation from "./CustomFullNamePopUpTabs/GeneralInformation/GeneralInformation";
import Documents from "./CustomFullNamePopUpTabs/Documents/Documents";
import History from "./CustomFullNamePopUpTabs/History/History";
import Comments from "./CustomFullNamePopUpTabs/Comments/Comments";
import { usePushNotifications } from "@/src/components/CustomUtils/PushNotifications/PushNotificationsProvider";
import { DocumentPreparationSubmissionMode } from "@/src/components/CustomUtils/CustomDocumentPreparationPopUp/CustomDocumentPreparationPopUp";
import { getEmailValidationError, getPhoneValidationError, isContactDraftValid } from "@/src/utils/contactValidation";

type IUserInfo = IRowData["userInfo"];

interface CustomFullNamePopUpProps {
  isApPopUpFullName: boolean;
  currentDataPopUp: IUserInfo | undefined;
  currentStudentId?: number;
  currentApplyInfoId?: number;
  tableContext?: "applications" | "tasks" | "office-accounting";
  className?: string;
  initialTabId?: string;
  onRefreshCurrentStudentUserInfo?: () => Promise<IRowData["userInfo"] | undefined>;
  onRefreshTableData?: () => Promise<void>;
  setIsApPopUpFullName: React.Dispatch<React.SetStateAction<boolean>>;
  setIsDocumentPreparationPopUpOpen?: React.Dispatch<React.SetStateAction<boolean>>;
  setDocumentPrepSubmissionMode?: React.Dispatch<React.SetStateAction<DocumentPreparationSubmissionMode>>;
}

const userInfoToApiField: Record<keyof IUserInfo, keyof IStudentUpdatePayload | null> = {
  documents: null,
  fullname: "fullname",
  fullnameNat: "fullname_nat",
  form_office: null,
  comments: null,
  history: null,
  email: "email",
  phone: "phone",
  course: "course",
  levelEducation: null,
  language: "language",
  country: "country",
  role: null,
  nationality: "nationality",
  residence: "residence",
  contactPerson: "contact_person",
  dateOfBirth: "birth_date",
  address: "address",
  passportNo: "passport_number",
  passportIssued: "passport_issued",
  validPassport: "valid_passport",
  accounting: null,
};

/** General Information fields that are logged to student history (order matches UI). */
const historyChangeLabels: { key: keyof IUserInfo; label: string }[] = [
  { key: "fullname", label: "Full name (EN)" },
  { key: "fullnameNat", label: "Full name (Nat.)" },
  { key: "email", label: "Email" },
  { key: "phone", label: "Phone" },
  { key: "course", label: "Course" },
  { key: "language", label: "Language" },
  { key: "country", label: "Country" },
  { key: "nationality", label: "Nationality" },
  { key: "residence", label: "Residence" },
  { key: "contactPerson", label: "Contact person" },
];

function buildHistoryChangeText(previous: IUserInfo, next: IUserInfo): string {
  const lines: string[] = [];
  for (const { key, label } of historyChangeLabels) {
    const fromVal = String(previous[key] ?? "");
    const toVal = String(next[key] ?? "");
    if (fromVal === toVal) continue;
    lines.push(`Changes have been made to the student card: ${label} ${fromVal} updated to ${toVal}`);
  }
  return lines.join("\n");
}

const CustomFullNamePopUp: FC<CustomFullNamePopUpProps> = ({
  isApPopUpFullName,
  currentDataPopUp,
  currentStudentId,
  currentApplyInfoId,
  tableContext = "applications",
  className,
  initialTabId = "general-information",
  onRefreshCurrentStudentUserInfo,
  onRefreshTableData,
  setIsApPopUpFullName,
  setIsDocumentPreparationPopUpOpen,
  setDocumentPrepSubmissionMode,
}) => {
  const dispatch = useDispatch();
  const { notify } = usePushNotifications();
  const applicationsTable = useSelector((state: RootState) => state.applicationsTable);
  const tasksTable = useSelector((state: RootState) => state.tasksTable);
  const officeAccountingTable = useSelector((state: RootState) => state.officeAccountingTable);
  const isTasksContext = tableContext === "tasks";
  const isOfficeAccountingContext = tableContext === "office-accounting";
  const data = isTasksContext ? tasksTable.data : isOfficeAccountingContext ? officeAccountingTable.data : applicationsTable.data;
  const selectedRows = isTasksContext
    ? tasksTable.selectedRows
    : isOfficeAccountingContext
      ? officeAccountingTable.selectedRows
      : applicationsTable.selectedRows;
  const isMoveDisabled = selectedRows.length !== 1;
  const currentApplyInfo = useMemo(() => {
    if (currentApplyInfoId == null) return undefined;
    if (isTasksContext) return tasksTable.data.find((item) => item.task_id === currentApplyInfoId);
    if (isOfficeAccountingContext) {
      return officeAccountingTable.data.find((item) => item.student_info_id === currentApplyInfoId);
    }
    return applicationsTable.data.find((item) => item.id === currentApplyInfoId);
  }, [applicationsTable.data, tasksTable.data, officeAccountingTable.data, currentApplyInfoId, isTasksContext, isOfficeAccountingContext]);
  const wasOpenRef = useRef(false);
  const [currentTabID, setCurrentTabID] = useState<string>("general-information");
  const [editor, setEditor] = useState<boolean>(false);

  const [currentEditorObj, setCurrentEditorObj] = useState<IUserInfo | undefined>();
  const [syncedUserInfo, setSyncedUserInfo] = useState<IUserInfo | undefined>(currentDataPopUp);
  const [editorDraft, setEditorDraft] = useState<IUserInfo | undefined>();
  const [showValidationErrors, setShowValidationErrors] = useState(false);
  const [pendingUpdatePayload, setPendingUpdatePayload] = useState<IStudentUpdatePayload>({});
  const hasPendingUpdates = Object.keys(pendingUpdatePayload).length > 0;

  useEffect(() => {
    if (!isApPopUpFullName) return;
    setCurrentTabID(initialTabId);
    setEditor(false);
  }, [isApPopUpFullName, initialTabId]);

  useEffect(() => {
    setSyncedUserInfo(currentDataPopUp);
  }, [currentDataPopUp]);

  useEffect(() => {
    setPendingUpdatePayload({});
  }, [currentDataPopUp, currentEditorObj]);

  const documentsUserInfo = useMemo(() => {
    const base = currentEditorObj ?? syncedUserInfo ?? currentDataPopUp;
    const documentsSource = syncedUserInfo ?? currentDataPopUp;
    if (!base || !documentsSource) return base;
    return { ...base, documents: documentsSource.documents };
  }, [currentEditorObj, syncedUserInfo, currentDataPopUp]);

  const refreshUserInfoInPopUp = useCallback(async () => {
    const nextUserInfo = await onRefreshCurrentStudentUserInfo?.();
    if (!nextUserInfo) return;
    setSyncedUserInfo(nextUserInfo);
    setCurrentEditorObj((prev) => (prev ? { ...prev, documents: nextUserInfo.documents } : prev));
  }, [onRefreshCurrentStudentUserInfo]);

  useEffect(() => {
    if (showValidationErrors && isContactDraftValid(editorDraft)) {
      setShowValidationErrors(false);
    }
  }, [editorDraft, showValidationErrors]);

  useEffect(() => {
    if (!isApPopUpFullName || !currentApplyInfoId) return;

    if (isTasksContext) {
      const currentRow = tasksTable.data.find((row) => row.task_id === currentApplyInfoId);
      if (!currentRow || selectedRows.includes(currentRow.task_id)) return;
      dispatch(toggleTasksRowSelection(currentRow.task_id));
      return;
    }
    if (isOfficeAccountingContext) {
      const currentRow = officeAccountingTable.data.find((row) => row.student_info_id === currentApplyInfoId);
      if (!currentRow || selectedRows.includes(currentRow.student_info_id)) return;
      dispatch(toggleOfficeAccountingRowSelection(currentRow.student_info_id));
      return;
    }
    const currentRow = applicationsTable.data.find((row) => row.id === currentApplyInfoId);
    if (!currentRow || selectedRows.includes(currentRow.id)) return;
    dispatch(toggleApplicationRowSelection(currentRow.id));
  }, [isApPopUpFullName, currentApplyInfoId, applicationsTable.data, tasksTable.data, officeAccountingTable.data, selectedRows, dispatch, isTasksContext, isOfficeAccountingContext]);

  useEffect(() => {
    const wasOpen = wasOpenRef.current;
    wasOpenRef.current = isApPopUpFullName;

    if (isApPopUpFullName || !wasOpen || !currentApplyInfoId) return;

    if (isTasksContext) {
      const currentRow = tasksTable.data.find((row) => row.task_id === currentApplyInfoId);
      if (!currentRow || !selectedRows.includes(currentRow.task_id)) return;
      dispatch(toggleTasksRowSelection(currentRow.task_id));
      return;
    }
    if (isOfficeAccountingContext) {
      const currentRow = officeAccountingTable.data.find((row) => row.student_info_id === currentApplyInfoId);
      if (!currentRow || !selectedRows.includes(currentRow.student_info_id)) return;
      dispatch(toggleOfficeAccountingRowSelection(currentRow.student_info_id));
      return;
    }
    const currentRow = applicationsTable.data.find((row) => row.id === currentApplyInfoId);
    if (!currentRow || !selectedRows.includes(currentRow.id)) return;
    dispatch(toggleApplicationRowSelection(currentRow.id));
  }, [isApPopUpFullName, currentApplyInfoId, applicationsTable.data, tasksTable.data, officeAccountingTable.data, selectedRows, dispatch, isTasksContext, isOfficeAccountingContext]);

  const handleStartEditorEvent = () => {
    setEditorDraft(currentEditorObj ?? currentDataPopUp);
    setShowValidationErrors(false);
    setEditor(true);
  };

  const handleEditorOkEvent = async () => {
    if (!editorDraft) {
      setEditor(false);
      return;
    }

    if (!isContactDraftValid(editorDraft)) {
      setShowValidationErrors(true);
      const emailError = getEmailValidationError(editorDraft.email);
      const phoneError = getPhoneValidationError(editorDraft.phone);
      const message = [emailError, phoneError].filter(Boolean).join(". ");
      notify({
        title: "Validation error",
        message: message || "Please check email and phone fields.",
        type: "error",
      });
      return;
    }

    const previous = currentEditorObj ?? currentDataPopUp;
    const changedPayload: IStudentUpdatePayload = {};
    if (previous) {
      (Object.keys(editorDraft) as Array<keyof IUserInfo>).forEach((key) => {
        if (editorDraft[key] === previous[key]) return;
        const apiField = userInfoToApiField[key];
        if (!apiField) return;
        changedPayload[apiField] = editorDraft[key] as never;
      });
    }

    if (previous) {
      const historyText = buildHistoryChangeText(previous, editorDraft);
      if (historyText && currentStudentId != null) {
        const historyResult = await applicantHistoryRequests.create({
          applicant_id: currentStudentId,
          text: historyText,
        });
        if (historyResult == null) {
          return;
        }
        const refreshedUserInfo = await onRefreshCurrentStudentUserInfo?.();
        if (refreshedUserInfo) {
          setSyncedUserInfo(refreshedUserInfo);
          setCurrentEditorObj((prev) => (prev ? { ...prev, documents: refreshedUserInfo.documents } : prev));
        }
      }
    }
    console.log("changedPayload", changedPayload);
    if (Object.keys(changedPayload).length > 0) {
      if (currentStudentId != null) {
        const updateResult = await applicantsRequests.update(currentStudentId, changedPayload as IApplicantUpdatePayload);
        if (updateResult == null) {
          return;
        }
        await onRefreshTableData?.();
      }
      setPendingUpdatePayload((prev) => ({ ...prev, ...changedPayload }));
    }

    setCurrentEditorObj(editorDraft);
    setEditor(false);
  };

  const handleEditorCloseEvent = () => {
    setEditorDraft(currentEditorObj ?? currentDataPopUp);
    setShowValidationErrors(false);
    setEditor(false);
  };

  return (
    <div
      className={`applications-page-popUp ${className ?? ""} ${isApPopUpFullName ? "show" : ""}`.trim()}
      data-has-pending-updates={hasPendingUpdates}
    >
      <div className="applications-page-popUp-header">
        <div className="applications-page-popUp-header-top">
          <div className="applications-page-popUp-header-top-title">
            <p>Full Information</p>
          </div>
          <div className="applications-page-popUp-header-top-buttons">
            {editor ? (
              <>
                <CustomDescriptionPopUP text="Validate and save changes to the applicant profile.">
                  <BtnDefault onlyIcon={true} onClick={() => handleEditorOkEvent()}>
                    <applicationPageSvgConst.EditorOkSvg />
                  </BtnDefault>
                </CustomDescriptionPopUP>
                <CustomDescriptionPopUP text="Discard unsaved changes and leave edit mode.">
                  <BtnDefault onlyIcon={true} onClick={() => handleEditorCloseEvent()}>
                    <applicationPageSvgConst.EditorCloseSvg />
                  </BtnDefault>
                </CustomDescriptionPopUP>
              </>
            ) : (
              <>
                {/* Stub actions without logic (commented out):
                <BtnDefault onlyIcon={true}>
                  <applicationPageSvgConst.PreviewSvg />
                </BtnDefault>
                */}
                <CustomDescriptionPopUP text="Edit the applicant's general information.">
                  <BtnDefault onlyIcon={true} onClick={() => handleStartEditorEvent()}>
                    <applicationPageSvgConst.CommentsSvg />
                  </BtnDefault>
                </CustomDescriptionPopUP>
                {/* Stub actions without logic (commented out):
                <BtnDefault onlyIcon={true}>
                  <applicationPageSvgConst.InfoSvg />
                </BtnDefault>
                <BtnDefault onlyIcon={true}>
                  <applicationPageSvgConst.DownloadSvg />
                </BtnDefault>
                <BtnDefault onlyIcon={true}>
                  <applicationPageSvgConst.UnreadSvg />
                </BtnDefault>
                <BtnDefault onlyIcon={true}>
                  <applicationPageSvgConst.AdmissionForwardSvg />
                </BtnDefault>
                <BtnDefault onlyIcon={true}>
                  <applicationPageSvgConst.TasksSvg />
                </BtnDefault>
                */}
                <CustomDescriptionPopUP text="Open document preparation for the selected record.">
                  <BtnDefault
                    onlyIcon={true}
                    disabled={isMoveDisabled}
                    onClick={() => {
                      if (isMoveDisabled) return;
                      setDocumentPrepSubmissionMode?.(isTasksContext ? "update" : "create");
                      setIsDocumentPreparationPopUpOpen?.(true);
                    }}
                  >
                    <applicationPageSvgConst.MoveSvg />
                  </BtnDefault>
                </CustomDescriptionPopUP>
                {/* Stub action without logic (commented out):
                <BtnDefault onlyIcon={true}>
                  <applicationPageSvgConst.DeleteSvg />
                </BtnDefault>
                */}
                <div className="applications-page-popUp-header-top-button close">
                  <CustomDescriptionPopUP text="Close the full information panel.">
                    <BtnDefault onlyIcon={true} onClick={() => setIsApPopUpFullName(false)}>
                      <img src={Arrow} alt="Arrow" />
                    </BtnDefault>
                  </CustomDescriptionPopUP>
                </div>
              </>
            )}
          </div>
        </div>
        <div className="applications-page-popUp-header-tabs">
          {onlineApplicationsPopUpTabs.map((tab, index) => (
            <div
              className={`applications-page-popUp-header-tabs-tab ${currentTabID === tab.id ? "active" : ""}`}
              key={index}
              onClick={() => setCurrentTabID(tab.id)}
            >
              <p>{tab.title}</p>
            </div>
          ))}
        </div>
      </div>
      <div className={`applications-page-popUp-body ${currentTabID}`}>
        {currentTabID === "general-information" && (
          <GeneralInformation
            viewData={currentEditorObj ?? currentDataPopUp}
            draftData={editorDraft}
            currentApplyInfo={currentApplyInfo as IApplyInfoData | ITasksTableRow | IOfficeAccountingTableRow | undefined}
            editor={editor}
            showValidationErrors={showValidationErrors}
            setEditorDraft={setEditorDraft}
          />
        )}
        {currentTabID === "documents" && (
          <Documents
            userInfo={documentsUserInfo}
            studentId={currentStudentId}
            onRefreshUserInfo={refreshUserInfoInPopUp}
          />
        )}
        {currentTabID === "comments" && (
          <Comments
            comments={syncedUserInfo?.comments ?? currentDataPopUp?.comments ?? []}
            applicantId={currentStudentId}
            onRefreshUserInfo={refreshUserInfoInPopUp}
          />
        )}
        {currentTabID === "history" && <History items={syncedUserInfo?.history ?? currentDataPopUp?.history ?? []} />}
      </div>
      <div className="applications-page-popUp-footer">
        <div className="applications-page-popUp-footer-title">
          <p>Executor</p>
        </div>
        <div className="applications-page-popUp-footer-users">
          <div className="applications-page-popUp-footer-users-user">
            <applicationPageSvgConst.UnknownUserSvg />
            <div className="applications-page-popUp-footer-users-user-status">
              <applicationPageSvgConst.StatusRedSvg />
            </div>
          </div>
          <div className="applications-page-popUp-footer-users-user">
            <applicationPageSvgConst.UnknownUserSvg />
            <div className="applications-page-popUp-footer-users-user-status">
              <applicationPageSvgConst.StatusRedSvg />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomFullNamePopUp;
